import { ReactNode, useState } from "react";
import {
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Combobox as HeadlessCombobox,
  ComboboxProps as HeadlessComboboxProps,
} from "@headlessui/react";
import { ExclamationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";

import { SearchIcon } from "~/common/components/icons";
import { ToolTip } from "~/common/components/ui/ToolTip";

interface ComboOption {
  value: string;
  label: string;
}

type SingleComboboxProps = HeadlessComboboxProps<string | null, false> & {
  multiple?: false;
};

type MultipleComboboxProps = HeadlessComboboxProps<string, true> & {
  multiple: true;
};

type ComboboxProps = (SingleComboboxProps | MultipleComboboxProps) & {
  id: string;
  options: ComboOption[];
  label?: string;
  placeholder?: string;
  tooltip?: ReactNode;
  unassignedLabel?: string;
  shouldShowUnassignedOption?: boolean;
};

export const Combobox = ({
  id,
  options,
  label,
  placeholder = "Search...",
  tooltip,
  unassignedLabel = "Unassigned",
  shouldShowUnassignedOption = true,
  ...props
}: ComboboxProps) => {
  const [query, setQuery] = useState("");
  const selectedValues =
    props.multiple && Array.isArray(props.value) ? props.value : [];

  const getOptionLabel = (value: string) => {
    return options.find((option) => option.value === value)?.label ?? "";
  };

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) => {
          return option.label.toLowerCase().includes(query.toLowerCase());
        });

  const comboboxContent = (
    <div className="relative">
      <div className="relative">
        <ComboboxInput
          id={id}
          placeholder={placeholder}
          displayValue={(value: string | string[] | null) => {
            if (props.multiple) {
              return query;
            }

            if (Array.isArray(value)) {
              return options
                .filter((option) => value.includes(option.value))
                .map((option) => option.label)
                .join(", ");
            }

            if (value === null) {
              return unassignedLabel;
            }

            return getOptionLabel(value);
          }}
          onChange={(event) => setQuery(event.target.value)}
          className="w-full rounded border border-gray-300 bg-neutral-25 p-3 text-sm font-light text-gray-700 focus:border-gray-20 focus:outline-none focus:ring-0"
        />
        <SearchIcon className="absolute right-3 top-1/2 -translate-y-1/2 transform" />
      </div>
      <ComboboxOptions
        anchor="bottom"
        className="z-50 mt-1 !max-h-60 rounded border border-gray-300 bg-neutral-25 text-sm font-light empty:invisible"
        style={{ width: "var(--input-width)" }}
      >
        {!props.multiple && shouldShowUnassignedOption && (
          <ComboboxOption
            value={null}
            className="data-focus:bg-blue-100 data-focus:ring-2 data-focus:ring-blue-100 cursor-pointer p-3 hover:bg-blue-50"
          >
            {({ selected }) => (
              <div className="flex items-center justify-between gap-2">
                <span>{unassignedLabel}</span>
                {props.multiple && (
                  <span
                    className={twMerge(
                      "font-medium text-primary-500",
                      selected && "text-gray-60",
                      !selected && "underline",
                    )}
                  >
                    {selected ? "Added" : "Add to list"}
                  </span>
                )}
              </div>
            )}
          </ComboboxOption>
        )}
        {filteredOptions.map((option) => (
          <ComboboxOption
            key={option.value}
            value={option.value}
            className="data-focus:bg-blue-100 cursor-pointer p-3 hover:bg-blue-50"
          >
            {({ selected }) => (
              <div className="flex items-center justify-between gap-2">
                <span>{option.label}</span>
                {props.multiple && (
                  <span
                    className={twMerge(
                      "font-medium text-primary-500",
                      selected && "text-gray-60",
                      !selected && "underline",
                    )}
                  >
                    {selected ? "Added" : "Add to list"}
                  </span>
                )}
              </div>
            )}
          </ComboboxOption>
        ))}
      </ComboboxOptions>
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      {props.multiple && selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-x-2 gap-y-2.5">
          {selectedValues.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-1 rounded-full bg-primary-10 px-3 py-1 text-sm font-medium text-primary-800"
            >
              <span>{getOptionLabel(value)}</span>
              <button
                type="button"
                aria-label={`Remove ${getOptionLabel(value)} from list`}
                className="rounded-full p-0.5 font-medium text-primary-800 hover:bg-primary-20"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  if (props.multiple) {
                    props.onChange?.(
                      selectedValues.filter(
                        (selectedValue) => selectedValue !== value,
                      ),
                    );
                  }
                }}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {(label ?? tooltip) && (
        <div className="flex items-center gap-1">
          {label && (
            <label htmlFor={id} className="text-sm font-medium text-gray-700">
              {label}
            </label>
          )}
          {tooltip && (
            <ToolTip
              className="bg-neutral-25 p-3.5"
              arrowClassName="bg-neutral-25"
              tooltip={tooltip}
            >
              <ExclamationCircleIcon className="h-5 w-5 rotate-180 stroke-2 text-gray-60" />
            </ToolTip>
          )}
        </div>
      )}

      {props.multiple ? (
        <HeadlessCombobox {...props} multiple onClose={() => setQuery("")}>
          {comboboxContent}
        </HeadlessCombobox>
      ) : (
        <HeadlessCombobox {...props} onClose={() => setQuery("")}>
          {comboboxContent}
        </HeadlessCombobox>
      )}
    </div>
  );
};
